import React from 'react';

/**
 * VarunAIq — brand wordmark + glyph.
 *
 * Varuna is the Vedic guardian of the cosmic ocean — the visual is a
 * stylised wave inside a soft circle, tinted in a cyan→blue gradient
 * to match the maritime / data-intelligence positioning.
 */

interface Props {
  size?:        'sm' | 'md' | 'lg';
  showTagline?: boolean;
  className?:   string;
}

const SIZES = {
  sm: { glyph: 'w-6 h-6',  word: 'text-base',  tag: 'text-[10px]' },
  md: { glyph: 'w-8 h-8',  word: 'text-xl',    tag: 'text-[11px]' },
  lg: { glyph: 'w-10 h-10', word: 'text-2xl',  tag: 'text-xs' },
};

export default function Brand({ size = 'md', showTagline = false, className = '' }: Props) {
  const s = SIZES[size];

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <WaveGlyph className={s.glyph} />
      <div className="leading-none">
        <div
          className={`font-bold tracking-tight bg-gradient-to-r from-cyan-300 via-cyan-400 to-blue-500 bg-clip-text text-transparent ${s.word}`}
        >
          Varuna<span className="font-light"> </span>IQ
        </div>
        {showTagline && (
          <div className={`text-gray-500 mt-1 tracking-wider uppercase ${s.tag}`}>
            Maritime Intelligence
          </div>
        )}
      </div>
    </div>
  );
}

/** Wave-in-circle glyph with cyan→blue gradient */
function WaveGlyph({ className }: { className: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id="vq-grad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#22d3ee" />
          <stop offset="60%"  stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#1e40af" />
        </linearGradient>
        <linearGradient id="vq-fill" x1="0" y1="32" x2="0" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="rgba(34,211,238,0.18)" />
          <stop offset="100%" stopColor="rgba(59,130,246,0.04)" />
        </linearGradient>
      </defs>

      {/* Outer ring */}
      <circle cx="16" cy="16" r="14.5" stroke="url(#vq-grad)" strokeWidth="1.4" fill="url(#vq-fill)" />

      {/* Three stylised waves */}
      <path d="M5 12 Q10 8 16 12 T27 12"
            stroke="url(#vq-grad)" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.45" />
      <path d="M5 17 Q10 13 16 17 T27 17"
            stroke="url(#vq-grad)" strokeWidth="2.0" fill="none" strokeLinecap="round" />
      <path d="M5 22 Q10 18 16 22 T27 22"
            stroke="url(#vq-grad)" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.55" />
    </svg>
  );
}
